import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Field, FieldTypeInfo, Group, Notice, NoticeAssignment, NoticeVersion, Site, SiteGroups } from '@cloudsign/shared';
import type { FieldScope } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';

export const FieldList = z.array(Field);
export const FieldTypeList = z.array(FieldTypeInfo);
export const NoticeList = z.array(Notice);
export const NoticeVersionList = z.array(NoticeVersion);
export const NoticeAssignmentList = z.array(NoticeAssignment);
export const GroupList = z.array(Group);

/** Where a list of fields lives: one site (sign-in / sign-out questions) or one group (personal fields). */
export type FieldTarget = { kind: 'site'; id: string } | { kind: 'group'; id: string };

export const fieldsPath = (target: FieldTarget): string => `/fields/${target.kind === 'site' ? 'sites' : 'groups'}/${target.id}`;

export const fieldsKey = (target: FieldTarget, scope: FieldScope) => ['fields', target.kind, target.id, scope] as const;

export function useFieldList(target: FieldTarget, scope: FieldScope, includeArchived: boolean) {
  return useQuery({
    queryKey: [...fieldsKey(target, scope), includeArchived],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get(fieldsPath(target), {
        query: { ...(target.kind === 'site' ? { scope } : {}), status: includeArchived ? 'all' : 'active' },
        schema: FieldList,
      }),
  });
}

export function useFieldTypes() {
  return useQuery({ queryKey: ['fields', 'types'], queryFn: () => api.get('/fields/types', { schema: FieldTypeList }), staleTime: 300_000 });
}

export function useSiteRecord(id: string) {
  return useQuery({ queryKey: ['sites', 'detail', id], queryFn: () => api.get(`/sites/${id}`, { schema: Site }) });
}

export function useGroupRecord(id: string) {
  return useQuery({ queryKey: ['groups', 'detail', id], queryFn: () => api.get(`/groups/${id}`, { schema: Group }) });
}

export function useAllGroups() {
  return useQuery({ queryKey: ['groups', 'list', 'active'], queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }) });
}

/** The groups that are visible at a site (all active groups, or the ones the site has chosen). */
export function useSiteGroups(siteId: string) {
  const groups = useAllGroups();
  const view = useQuery({ queryKey: ['sites', 'groups', siteId], queryFn: () => api.get(`/sites/${siteId}/groups`, { schema: SiteGroups }) });
  const data = groups.data && view.data ? (view.data.mode === 'all' ? groups.data : groups.data.filter((g) => view.data.groupIds.includes(g.id))) : undefined;
  return { data, isLoading: groups.isLoading || view.isLoading, error: groups.error ?? view.error };
}
